import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, IconButton, Pagination, styled, Tooltip, Typography } from '@mui/material'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { ReactComponent as EyeIcon } from '../../../assets/svg/eye.svg';
import { ReactComponent as Trash } from '../../../assets/svg/trash.svg';
import { deleteLicense, getLicense } from '../../../api/license';
import { showErrorToast, showSuccessToast } from '../../../toastUtils';
import CommonHeader from '../../../CommonComponents/CommonHeader';
import CommonConfirmationModal from '../../../CommonComponents/CommonConfirmationModal';
import SearchInput from '../../../CommonComponents/SearchInput';
import DateFilterPicker from '../../../CommonComponents/DateFilterPicker';
import ButtonCommon from '../../../CommonComponents/ButtonCommon';
import { Add } from '@mui/icons-material';
import ComponentWrapper from '../../../CommonComponents/ComponentWrapper';

export default function LicensePlans() {
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: "#F9FAFB",
            color: "#000000",
            fontWeight: 600,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
    }));
    const [licenseData, setLicenseData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [dateValue, setDateValue] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [paginatedData, setPaginatedData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const fetchLicenseData = async () => {
        setLoading(true);
        try {
            const response = await getLicense(page, pageSize);
            if (response.success) {
                setLicenseData(response.data.plan_details || []);
                setFilteredData(response.data.plan_details || []);
                setPaginatedData(response.data.plan_details || []);
                setTotalCount(response.data.total_plans || 0);
            }
        } catch (error) {
            console.error('Error fetching license data:', error);
            setPaginatedData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLicenseData();
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDeleteLicense = (id) => {
        setDeleteDialogOpen(true);
        setDeleteId(id)
    }

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true)
        if (!deleteId) {
            console.error('License ID is required for deletion');
            return;
        }
        try {
            const response = await deleteLicense(deleteId);
            if (response.success) {
                setLicenseData((prev) => prev.filter((user) => user.user_id !== deleteId));
                showSuccessToast(response.message);
                fetchLicenseData()
            }
        } catch (error) {
            console.error('Failed to delete license:', error);
            showErrorToast('Failed to delete license');
        } finally {
            setDeleteId(null)
            setDeleteLoading(false)
            setDeleteDialogOpen(false);
        }
    };

    useEffect(() => {
        const data = paginatedData?.length ? paginatedData : [];

        const filtered = data.filter((item) => {
            const planName = item.plan_name?.toLowerCase() || '';
            const search = searchValue?.toLowerCase() || '';
            const isNameMatch = planName.includes(search);

            const isDateMatch = dateValue
                ? new Date(item.expiry_date).toDateString() === new Date(dateValue).toDateString()
                : true;


            return isNameMatch && isDateMatch;
        });

        setFilteredData(filtered);
    }, [searchValue, dateValue, licenseData, paginatedData]);

    return (
        <ComponentWrapper>
            {/* <Stack sx={{ maxWidth: '1000px' }}> */}
            <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <CommonHeader
                        title="License Plan"
                        subTitle="You can add, update or delete the plans on this page."
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'end', mb: 2 }}>
                    <SearchInput
                        placeholder="Search by Name"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    <DateFilterPicker
                        value={dateValue}
                        onChange={setDateValue}
                        sx={{ maxWidth: 250 }}
                        label="Filter by Date"
                    />
                    <ButtonCommon icon={<Add />} onClick={() => navigate('/customer/license/create')}>
                        Create
                    </ButtonCommon>
                </Box>
            </Box>
            <Box>
                <TableContainer component={Paper}
                    variant="outlined"
                    sx={{ borderRadius: "12px" }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#DFDFDF' }}>
                                <StyledTableCell>Plan Name</StyledTableCell>
                                <StyledTableCell>Expiry Date</StyledTableCell>
                                <StyledTableCell>Active Orgs</StyledTableCell>
                                <StyledTableCell>Linked Orgs</StyledTableCell>
                                <StyledTableCell>Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : filteredData && filteredData.length > 0 ? (filteredData.map((row) => (
                                <TableRow
                                    key={row.plan_id}
                                    hover
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: '#FFE5DD',
                                                    color: '#FF6348',
                                                    width: '30px',
                                                    height: '30px',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {row.plan_name.charAt(0).toUpperCase()}
                                            </Box>
                                            <Typography sx={{ fontSize: '14px' }}>{row.plan_name}</Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ fontSize: '14px' }}>{row.expiry_date}</StyledTableCell>
                                    <StyledTableCell sx={{ fontSize: '14px', paddingLeft: 3 }}>{row.active_orgs}</StyledTableCell>
                                    <StyledTableCell sx={{ fontSize: '14px', paddingLeft: 3 }}>{row.linked_orgs}</StyledTableCell>
                                    <StyledTableCell sx={{ px: 0 }}>
                                        <Tooltip title="View" arrow>
                                            <IconButton onClick={() => navigate(`/customer/license/view/${row.plan_id}`)}>
                                                <EyeIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Delete" arrow onClick={() => handleDeleteLicense(row.plan_id)}>
                                            <IconButton disabled={row.linked_orgs > 0 || row.active_orgs > 0} sx={{ color: (row.linked_orgs > 0 || row.active_orgs > 0) ? '#ccc' : '' }}>
                                                <Trash />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </TableRow>
                            ))) : (
                                <TableRow>
                                    <StyledTableCell colSpan={4}>
                                        <Typography
                                            sx={{
                                                textAlign: 'center',
                                                py: 2,
                                                color: 'text.secondary',
                                                fontSize: '14px',
                                            }}
                                        >
                                            No data available
                                        </Typography>
                                    </StyledTableCell>
                                </TableRow>)}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={Math.ceil(totalCount / pageSize)}
                        page={page}
                        onChange={(event, value) => setPage(value)}
                        color="primary"
                        shape="rounded"
                        variant="outlined"
                    />
                </Box>
            </Box>

            <CommonConfirmationModal
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="Confirm Delete"
                description={`Are you sure you want to delete the plan?`}
                loading={deleteLoading}
                confirmLabel="Delete"
                cancelLabel="Cancel"
            />
            {/* </Stack> */}
        </ComponentWrapper>
    )
}
